import { useContext } from 'react';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../../../../common/global/helpers';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import Button from '../../../../../common/widgets/Button';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import PopoverDialog from '../../../../../common/widgets/PopoverDialog';
import { config } from '../../../config';
import { paymentMethodCodes } from '../../../constants';
import { CustomerBudgetContext } from '../../../contexts/CustomerBudgetContext';
import { CheckOutWarningType } from '../../../enums';
import { CanCheckOutModel, CartSummaryModel } from '../../../models/Cart';
import { resources } from '../../../resources/resources';
import { CartPriceLoader } from './CartPriceLoader';
import {
  getBudgetTaxesMessage,
  getSalaryTaxesMessage,
  isGrossup,
} from './helper';
import { Budget } from '../../../contexts/common';

interface CartSummaryProps {
  summary: CartSummaryModel;
  useBenefitBudgetList: number[];
  isCheckoutButtonLoading: boolean;
  isSummaryLoading: boolean;
  handleCheckout: () => Promise<void>;
  canCheckout: (
    summary: CartSummaryModel,
    customerBudget: Budget,
  ) => CanCheckOutModel;
}

export const CartSummary = (props: CartSummaryProps) => {
  const {
    summary,
    isCheckoutButtonLoading,
    isSummaryLoading,
    handleCheckout,
    canCheckout,
  } = props;
  const { customerBudget } = useContext(CustomerBudgetContext);
  const { customerInfo } = useContext(CustomerInfoContext);
  const { isTablet } = useBreakpoints();
  const { t: translate } = useTranslation();

  const canCheckoutResult = canCheckout(summary, customerBudget);

  return (
    <div className="area-summary">
      <h2>{translate(resources.cart.title.summary)}</h2>
      <div>
        <ul className="checkout-summary-top list-reset">
          <li>
            <span className="summary-label">
              {`${
                summary.products.length > 1 ||
                (summary.products.length == 1 &&
                  summary.products[0].quantity > 1)
                  ? translate(resources.cart.summary.netProductsCost)
                  : translate(resources.cart.summary.netProductCost)
              } `}
            </span>
            <span className="summary-value">
              <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
                {formatPrice(summary.cartTotal)} {config.currency}
              </Loader>
            </span>
          </li>
          {customerInfo.platformType === 1 && (
            <li>
              <span className="summary-label">
                {translate(resources.cart.summary.budgetTaxes.label)}
                <PopoverDialog
                  className="checkout-info-dropdown"
                  placement="right"
                  closeFromContent
                  type="hover"
                  content={
                    <span>
                      {translate(
                        getBudgetTaxesMessage(
                          summary.budgetTaxes,
                          isGrossup(summary.products),
                        ),
                      )}
                    </span>
                  }
                >
                  <a className="btn cart-info-btn">
                    <span className="btn-icon">
                      <Icons.InfoSummary />
                    </span>
                  </a>
                </PopoverDialog>
              </span>
              <span className="summary-value">
                <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
                  {formatPrice(summary.budgetTaxes)} {config.currency}
                </Loader>
              </span>
            </li>
          )}
          {!!summary.cashbackDiscount && (
            <li>
              <span className="summary-label">
                {`${translate(resources.cart.summary.cashback.label)} `}
              </span>
              <span className="summary-value cashback-value">
                <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
                  -{formatPrice(summary.cashbackDiscount)} {config.currency}
                </Loader>
              </span>
            </li>
          )}
        </ul>

        <div className="summary-separator">
          <Icons.SolidSeparator />
        </div>

        {(summary.paidFromBudget || summary.paidFromBudget !== 0) && (
          <div className="checkout-summary-bottom">
            <div className="summary-left">
              <span className="summary-title">
                {translate(resources.cart.title.total)}
              </span>
              <span className="summary-label">
                <strong>{`${translate(
                  resources.cart.summary.total.fromBudget,
                )} `}</strong>
              </span>
            </div>

            <span className="summary-value">
              <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
                {formatPrice(summary.paidFromBudget)} {config.currency}
              </Loader>
            </span>
          </div>
        )}

        {summary.paidFromContribution !== 0 && (
          <div className="checkout-summary-bottom">
            <div className="summary-left">
              <span className="summary-title">
                {translate(resources.cart.title.total)}
              </span>
              <span className="summary-label">
                <strong>
                  {summary.paymentMethods.some(
                    pm => pm.code === paymentMethodCodes.salaryDeduction,
                  )
                    ? translate(resources.cart.summary.total.fromNextSalary)
                    : translate(resources.cart.summary.total.withCard)}
                </strong>
              </span>
            </div>
            <span className="summary-value">
              <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
                {formatPrice(summary.paidFromContribution)} {config.currency}
              </Loader>
            </span>
          </div>
        )}

        {summary.paidFromContribution === 0 &&
          summary.paidFromBudget === 0 &&
          summary.cashbackDiscount > 0 && (
            <div className="checkout-summary-bottom">
              <div className="summary-left">
                <span className="summary-title">
                  {translate(resources.cart.title.total)}
                </span>
              </div>
              <span className="summary-value">0 {config.currency}</span>
            </div>
          )}

        {customerInfo.platformType === 1 && (
          <div className="summary-separator">
            <Icons.DashedSeparator />
          </div>
        )}

        {customerInfo.platformType === 1 && (
          <ul className="checkout-summary-top list-reset">
            <li>
              <span className="summary-label">
                {translate(resources.cart.summary.salaryTaxes.label)}
                <PopoverDialog
                  className="checkout-info-dropdown"
                  placement="right"
                  closeFromContent
                  type="hover"
                  content={
                    <span>
                      {translate(
                        getSalaryTaxesMessage(
                          summary.salaryTaxes,
                          summary.budgetTaxes > 0,
                        ),
                      )}
                    </span>
                  }
                >
                  <a className="btn cart-info-btn">
                    <span className="btn-icon">
                      <Icons.InfoSummary />
                    </span>
                  </a>
                </PopoverDialog>
              </span>
              <span className="summary-value">
                <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
                  {formatPrice(summary.salaryTaxes)} {config.currency}
                </Loader>
              </span>
            </li>
          </ul>
        )}

        {/* Button desktop */}
        {!isTablet && (
          <div className="checkout-action">
            <Button
              className="btn btn-solid btn-lg btn-full"
              onClick={handleCheckout}
              disabled={!canCheckoutResult.value}
              loading={isCheckoutButtonLoading}
            >
              <span className="btn-text">
                {translate(resources.cart.summary.placeOrder)}
              </span>
            </Button>
          </div>
        )}

        {isTablet && (
          <div className="checkout-action">
            <Button
              className="btn btn-solid btn-lg btn-full"
              onClick={handleCheckout}
              disabled={!canCheckoutResult.value}
              loading={isCheckoutButtonLoading}
            >
              <span className="btn-text">
                {translate(resources.cart.summary.placeOrder)}
              </span>
            </Button>
          </div>
        )}
        {!canCheckoutResult.value &&
          canCheckoutResult.warningType ===
            CheckOutWarningType.NoPaymentMethodSelected && (
            <div className="checkout-message">
              <span>
                <Icons.InfoMedium />
              </span>
              <span>
                {translate(resources.cart.warning.noPaymentMethodSelected)}
              </span>
            </div>
          )}
      </div>
    </div>
  );
};
